import { BrowserClient, Hub } from '@sentry/browser';
import * as Integrations from '@sentry/integrations';
import type { Scope } from '@sentry/types';

import { getIdentityEnv } from '@atlassian/atlassian-studio-env-utils';

import type { ExtrasAndTags } from './types';

const DEFAULT_SENTRY_DSN =
	'https://e10ca40a18c948bfa4f88f0d371e6b39@o55978.ingest.us.sentry.io/4510779535654912';
const DEFAULT_PACKAGE_NAME = '@atlassian/twg-query-builder';
const DEFAULT_TEAM_NAME = 'studio-fe-twg-graphyte';
const DEFAULT_PRODUCT = 'twgQueryBuilder';
const DEFAULT_LOGGER = 'logger.default';

/**
 * Singleton instance of the isolated TWG Sentry Hub.
 *
 * Uses lazy initialization pattern - the hub is only created on first use
 * (when logException/logMessage/setGlobalTraceIds is called), not at module load time.
 *
 * This approach:
 * 1. Prevents interference with global Sentry.init() from other packages
 * 2. Ensures only one Sentry client exists for TWG (singleton)
 * 3. Avoids unnecessary initialization if Sentry features are never used
 * 4. The instance itself acts as the guard (null check) - no separate boolean flag needed
 */
let twgHub: Hub | null = null;

export type SentryClientConfig = {
	packageName?: string;
	sentryDsn?: string;
};

/**
 * Get or create the isolated TWG Sentry hub.
 * This creates a completely separate Sentry client that doesn't
 * interfere with any global Sentry.init() calls.
 *
 * Per Sentry best practices for shared environments:
 * https://docs.sentry.io/platforms/javascript/best-practices/multiple-sentry-instances/
 * https://docs.sentry.io/platforms/javascript/guides/electron/best-practices/shared-environments/
 */
const getOrCreateTwgHub = (config?: SentryClientConfig): Hub => {
	if (twgHub) {
		return twgHub;
	}

	const packageName = config?.packageName || DEFAULT_PACKAGE_NAME;
	const sentryDsn = config?.sentryDsn || DEFAULT_SENTRY_DSN;

	const environment = window.location.href.includes('localhost')
		? 'dev'
		: getIdentityEnv() === 'prod'
			? 'production'
			: getIdentityEnv();

	// Create isolated client - does NOT use Sentry.init()
	// This prevents interference with any other Sentry clients (e.g., atlassian-studio-observability)
	const client = new BrowserClient({
		dsn: sentryDsn,
		environment: environment,
		enabled: environment !== 'dev',
		beforeSend: (event) => {
			// We only want errors captured explicitly via logException/logMessage, to avoid unwanted errors being sent
			if (event.tags?.name === undefined) {
				return null;
			}
			return event;
		},
		integrations: [new Integrations.ExtraErrorData()],
		// Note: We don't include BrowserApiErrors, Breadcrumbs, or GlobalHandlers
		// as these use global state and would conflict with other Sentry instances
		initialScope: {
			user: {
				id: 'UNKNOWN_AAID',
			},
			tags: {
				packageName: packageName,
				teamName: DEFAULT_TEAM_NAME,
				product: DEFAULT_PRODUCT,
				logger: DEFAULT_LOGGER,
			},
		},
		attachStacktrace: true,
		// https://github.com/getsentry/sentry-javascript/issues/2292
		ignoreErrors: ['Non-Error exception captured'],
	});

	// Create isolated hub with this client
	// The hub provides scope management without touching the global Sentry state
	twgHub = new Hub(client);

	return twgHub;
};

const setTagsFromExtras = (scope: Scope, extrasAndTags: ExtrasAndTags): void => {
	scope.setTag('name', DEFAULT_PRODUCT);

	if (extrasAndTags.loggerName !== undefined && extrasAndTags.loggerName.length !== 0) {
		scope.setTag('logger', `logger.${extrasAndTags.loggerName}`);
	}
	if (extrasAndTags.accountId !== undefined && extrasAndTags.accountId.length !== 0) {
		scope.setUser({ id: extrasAndTags.accountId });
	}
	if (extrasAndTags.teamName !== undefined && extrasAndTags.teamName.length !== 0) {
		scope.setTag('teamName', extrasAndTags.teamName);
	}
	if (extrasAndTags.touchpointId !== undefined && extrasAndTags.touchpointId.length !== 0) {
		scope.setTag('touchpointId', extrasAndTags.touchpointId);
	}
	// Trace ID fields
	if (extrasAndTags.nl2CypherTraceId !== undefined && extrasAndTags.nl2CypherTraceId.length !== 0) {
		scope.setTag('nl2CypherTraceId', extrasAndTags.nl2CypherTraceId);
	}
	if (
		extrasAndTags.querySuggestionsTraceId !== undefined &&
		extrasAndTags.querySuggestionsTraceId.length !== 0
	) {
		scope.setTag('querySuggestionsTraceId', extrasAndTags.querySuggestionsTraceId);
	}
};

export type TraceIds = {
	nl2CypherTraceId: string | null;
	querySuggestionsTraceId: string | null;
};

export const setGlobalTraceIds = (traceIds: TraceIds): void => {
	const hub = getOrCreateTwgHub();

	hub.configureScope((scope) => {
		if (traceIds.nl2CypherTraceId) {
			scope.setTag('nl2CypherTraceId', traceIds.nl2CypherTraceId);
		} else {
			scope.setTag('nl2CypherTraceId', undefined);
		}

		if (traceIds.querySuggestionsTraceId) {
			scope.setTag('querySuggestionsTraceId', traceIds.querySuggestionsTraceId);
		} else {
			scope.setTag('querySuggestionsTraceId', undefined);
		}
	});
};

export const logException = (exception: Error | unknown, extrasAndTags?: ExtrasAndTags): void => {
	const hub = getOrCreateTwgHub();

	hub.withScope((scope) => {
		scope.setTag('name', DEFAULT_PRODUCT);

		if (extrasAndTags) {
			scope.setExtras(extrasAndTags);
			setTagsFromExtras(scope, extrasAndTags);
		}
		hub.captureException(exception);
	});
};

export const logMessage = (message: string, extrasAndTags?: ExtrasAndTags): void => {
	const hub = getOrCreateTwgHub();

	hub.withScope((scope) => {
		scope.setTag('name', DEFAULT_PRODUCT);

		if (extrasAndTags) {
			scope.setExtras(extrasAndTags);
			setTagsFromExtras(scope, extrasAndTags);
		}
		hub.captureMessage(message);
	});
};
